<template>
  <div class="schedule-timeline">
    <div class="schedule-timeline__header">
      <div class="schedule-timeline__title">{{ title }}</div>
    </div>

    <div class="schedule-timeline__scale">
      <div
        v-for="marker in markers"
        :key="marker.label"
        class="schedule-timeline__marker"
        :style="{ left: `${marker.left}%` }"
      >
        <span class="schedule-timeline__marker-label">{{ marker.label }}</span>
      </div>

      <div
        v-for="bar in bars"
        :key="bar.key"
        class="schedule-timeline__bar"
        :class="{ 'schedule-timeline__bar--split': bar.split }"
        :style="bar.style"
      >
        <span v-if="bar.showLabel" class="schedule-timeline__bar-label">
          {{ bar.label }}
        </span>
      </div>

      <div v-if="!intervals.length" class="schedule-timeline__empty">No intervals</div>
    </div>

    <div class="schedule-timeline__intervals">
      <div class="schedule-timeline__intervals-header">
        <span>Intervals</span>
        <a-button size="small" icon="plus" @click="$emit('add')"> Add interval </a-button>
      </div>

      <div v-if="intervals.length" class="schedule-timeline__interval-list">
        <div
          v-for="(interval, index) in intervals"
          :key="getIntervalKey(interval, index)"
          class="schedule-timeline__interval-row"
        >
          <span
            class="schedule-timeline__interval-marker"
            :style="{ backgroundColor: getIntervalColorForInterval(interval, index) }"
          ></span>

          <div class="schedule-timeline__interval-info">
            <div class="schedule-timeline__interval-time">
              {{ formatInterval(interval) }}
            </div>
            <div class="schedule-timeline__interval-meta">
              {{ formatDuration(interval) }}
              <span v-if="interval.continues_from_previous_day"> - from previous day </span>
              <span v-else-if="interval.continues_next_day"> - continues next day </span>
              <span v-else-if="interval.cross_boundary"> - crosses midnight</span>
            </div>
          </div>

          <div class="schedule-timeline__interval-actions">
            <a-button size="small" icon="edit" @click="$emit('edit', getIntervalPayload(interval, index))">
              Edit
            </a-button>
            <a-button
              size="small"
              icon="delete"
              type="danger"
              ghost
              @click="$emit('clear', getIntervalPayload(interval, index))"
            >
              Clear
            </a-button>
          </div>
        </div>
      </div>

      <div v-else class="schedule-timeline__interval-empty">No intervals added yet</div>
    </div>
  </div>
</template>

<script>
import { Button } from 'ant-design-vue'
import { DAY_MINUTES, timeToMinutes } from './schedule-utils'

const INTERVAL_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899']

export default {
  components: {
    'a-button': Button
  },
  props: {
    title: {
      type: String,
      required: true
    },
    intervals: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    markers() {
      return [
        { label: 'AM', left: (7 * 60 * 100) / DAY_MINUTES },
        { label: 'PM', left: (15 * 60 * 100) / DAY_MINUTES },
        { label: 'NOC', left: (23 * 60 * 100) / DAY_MINUTES }
      ]
    },

    bars() {
      return this.intervals.flatMap((interval, index) => {
        const start = this.getTimelineStart(interval)
        const end = this.getTimelineEnd(interval)
        const crosses = !interval._disable_local_split && (interval.cross_boundary || end <= start)
        const label = this.formatInterval(interval)

        if (!crosses) {
          return [
            this.createBar({
              key: `${index}-main`,
              start,
              end,
              label,
              interval,
              intervalIndex: index,
              showLabel: true
            })
          ]
        }

        return [
          this.createBar({
            key: `${index}-first`,
            start,
            end: DAY_MINUTES,
            label,
            interval,
            intervalIndex: index,
            showLabel: true,
            split: true
          }),
          this.createBar({
            key: `${index}-second`,
            start: 0,
            end,
            label,
            interval,
            intervalIndex: index,
            showLabel: false,
            split: true
          })
        ]
      })
    }
  },
  methods: {
    createBar({ key, start, end, label, interval, intervalIndex, showLabel, split = false }) {
      const left = (start * 100) / DAY_MINUTES
      const width = Math.max(((end - start) * 100) / DAY_MINUTES, 1)

      return {
        key,
        label,
        intervalIndex,
        split,
        showLabel: showLabel && width >= 10,
        style: {
          left: `${left}%`,
          width: `${width}%`,
          backgroundColor: this.getIntervalColorForInterval(interval, intervalIndex)
        }
      }
    },

    getTimelineStart(interval) {
      return typeof interval._timeline_start_minutes === 'number'
        ? interval._timeline_start_minutes
        : timeToMinutes(interval.start)
    },

    getTimelineEnd(interval) {
      return typeof interval._timeline_end_minutes === 'number'
        ? interval._timeline_end_minutes
        : timeToMinutes(interval.end)
    },

    getIntervalPayload(interval, index) {
      return interval._source ?? index
    },

    getIntervalKey(interval, index) {
      if (interval._source) {
        return `${interval._source.dayKey}-${interval._source.index}-${interval.start}-${interval.end}`
      }

      return `${interval.start}-${interval.end}-${index}`
    },

    formatInterval(interval) {
      return `${interval.start || '--:--'} - ${interval.end || '--:--'}`
    },

    formatDuration(interval) {
      const minutes = interval.duration_in_minutes

      if (!minutes && minutes !== 0) {
        return 'Duration not set'
      }

      const hours = Math.floor(minutes / 60)
      const restMinutes = minutes % 60

      if (!hours) {
        return `${restMinutes} min`
      }

      if (!restMinutes) {
        return `${hours} h`
      }

      return `${hours} h ${restMinutes} min`
    },

    getIntervalColor(index) {
      return INTERVAL_COLORS[index % INTERVAL_COLORS.length]
    },

    getIntervalColorForInterval(interval, index) {
      const colorIndex = typeof interval._color_index === 'number' ? interval._color_index : index

      return this.getIntervalColor(colorIndex)
    }
  }
}
</script>

<style scoped>
.schedule-timeline {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-timeline__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.schedule-timeline__title {
  color: var(--text-color-alpha8);
  font-size: 15px;
  font-weight: 600;
}

.schedule-timeline__scale {
  position: relative;
  height: 48px;
  margin: 22px 0;
  overflow: visible;
  border: 1px solid var(--border-color-split);
  background:
    repeating-linear-gradient(
      to right,
      transparent 0,
      transparent calc(100% / 48 - 1px),
      var(--border-color-split) calc(100% / 48 - 1px),
      var(--border-color-split) calc(100% / 48)
    ),
    var(--bg-table-header);
}

.schedule-timeline__marker {
  position: absolute;
  top: -22px;
  bottom: -10px;
  width: 2px;
  background-color: var(--error-color);
  transform: translateX(-1px);
  z-index: 2;
}

.schedule-timeline__marker-label {
  position: absolute;
  top: -22px;
  left: 50%;
  color: var(--error-color);
  font-size: 12px;
  font-weight: 700;
  transform: translateX(-50%);
}

.schedule-timeline__bar {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 4px;
  background-color: var(--success-color);
  color: #fff;
  font-weight: 600;
  z-index: 1;
}

.schedule-timeline__bar--split {
  opacity: 0.85;
}

.schedule-timeline__bar-label {
  overflow: hidden;
  padding: 0 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.schedule-timeline__empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-alpha4);
  font-size: 13px;
}

.schedule-timeline__intervals {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.schedule-timeline__intervals-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-color-alpha6);
  font-size: 13px;
  font-weight: 600;
}

.schedule-timeline__interval-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 8px;
}

.schedule-timeline__interval-row {
  display: grid;
  grid-template-columns: 4px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  padding: 8px 10px 8px 0;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
  background-color: var(--bg-component);
  color: var(--text-color);
}

.schedule-timeline__interval-marker {
  align-self: stretch;
  border-radius: var(--border-radius) 0 0 var(--border-radius);
}

.schedule-timeline__interval-info {
  min-width: 0;
}

.schedule-timeline__interval-time {
  overflow: hidden;
  color: var(--text-color);
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.schedule-timeline__interval-meta {
  margin-top: 4px;
  color: var(--text-color-alpha6);
  font-size: 12px;
}

.schedule-timeline__interval-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.schedule-timeline__interval-empty {
  padding: 12px;
  border: 1px dashed var(--border-color-base);
  border-radius: var(--border-radius);
  color: var(--text-color-alpha4);
  text-align: center;
}

@media (max-width: 768px) {
  .schedule-timeline__interval-row {
    grid-template-columns: 4px minmax(0, 1fr);
  }

  .schedule-timeline__interval-actions {
    grid-column: 2;
    justify-content: flex-start;
  }
}
</style>
